import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  createVideoRoom,
  deleteVideoRoom,
  updateVideoRoom,
  extendVideoRoom,
  endVideoRoom,
  getVideoRoomByMeetingId,
  type VideoRoom,
  type CreateVideoRoomParams,
} from '../services/dailyService'
import { meetingKeys } from './useMeetings'

// ===== QUERY KEYS =====
export const videoRoomKeys = {
  all: ['video-rooms'] as const,
  detail: (meetingId: string) => [...videoRoomKeys.all, 'detail', meetingId] as const,
}

// ===== HOOKS =====

/**
 * Récupère la room vidéo associée à une AG
 */
export function useVideoRoom(meetingId: string | undefined) {
  return useQuery({
    queryKey: videoRoomKeys.detail(meetingId!),
    queryFn: async (): Promise<VideoRoom | null> => {
      return getVideoRoomByMeetingId(meetingId!)
    },
    enabled: !!meetingId,
  })
}

/**
 * Crée une room vidéo pour une AG
 */
export function useCreateVideoRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: CreateVideoRoomParams): Promise<VideoRoom> => {
      return createVideoRoom(params)
    },
    onSuccess: (data, variables) => {
      // Mettre à jour le cache de la video room
      queryClient.setQueryData(videoRoomKeys.detail(variables.meetingId), data)
      // Invalider le meeting pour récupérer video_room_url
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(variables.meetingId) })
    },
  })
}

/**
 * Met à jour une room vidéo (report d'AG)
 */
export function useUpdateVideoRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      roomName,
      newMeetingDate,
      apiKey,
    }: {
      meetingId: string
      roomName: string
      newMeetingDate: Date
      apiKey: string
    }): Promise<void> => {
      await updateVideoRoom({ roomName, newMeetingDate, apiKey })
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: videoRoomKeys.detail(variables.meetingId) })
    },
  })
}

/**
 * Supprime une room vidéo (annulation d'AG)
 */
export function useDeleteVideoRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      meetingId,
      roomName,
      apiKey,
    }: {
      meetingId: string
      roomName: string
      apiKey: string
    }): Promise<void> => {
      await deleteVideoRoom(meetingId, roomName, apiKey)
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(videoRoomKeys.detail(variables.meetingId), null)
      queryClient.invalidateQueries({ queryKey: meetingKeys.detail(variables.meetingId) })
    },
  })
}

/**
 * Prolonge une room vidéo
 */
export function useExtendVideoRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      roomName,
      extensionHours,
      apiKey,
    }: {
      meetingId: string
      roomName: string
      extensionHours: number
      apiKey: string
    }): Promise<void> => {
      await extendVideoRoom(roomName, extensionHours, apiKey)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: videoRoomKeys.detail(variables.meetingId) })
    },
  })
}

/**
 * Termine une room vidéo
 */
export function useEndVideoRoom() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (meetingId: string): Promise<void> => {
      await endVideoRoom(meetingId)
    },
    onSuccess: (_, meetingId) => {
      queryClient.invalidateQueries({ queryKey: videoRoomKeys.detail(meetingId) })
    },
  })
}

/**
 * Vérifie si le meeting a la visio activée
 */
export function useIsVideoEnabled(meetingId: string | undefined) {
  return useQuery({
    queryKey: [...videoRoomKeys.all, 'is-enabled', meetingId],
    queryFn: async (): Promise<boolean> => {
      // Note: Cast temporaire - les types Supabase seront mis à jour après exécution de la migration
      const { data, error } = await (supabase as any)
        .from('meetings')
        .select('video_enabled')
        .eq('id', meetingId!)
        .single()

      if (error) return false
      return data?.video_enabled ?? false
    },
    enabled: !!meetingId,
  })
}
